import { ActionFunctionArgs, LoaderFunctionArgs } from "react-router-dom";
import { loadCookiesData, INTENTS } from "~/utils";
import * as APITypes from "~/API";
import { getAuth } from "firebase/auth";
import { getReadyAuthUser } from "~/utils/auth/waitForAuthReady";
import {
  apiClient,
  LifecycleAccessTabLoaderResult,
} from "~/services/apiClient";

/**
 * Loader for LifecycleAccessTab
 * Fetches all lifecycle access rights for a specific member
 */
export async function LifecycleAccessTabLoader({
  params,
}: LoaderFunctionArgs): Promise<LifecycleAccessTabLoaderType> {
  const auth = getAuth();
  const user = await getReadyAuthUser();

  const authAccessInfo = await loadCookiesData(user?.uid || "");

  const memberID = params.userID || "";

  const result = await apiClient.loader<LifecycleAccessTabLoaderResult>(
    "lifecycle-access-tab",
    { id: memberID },
  );

  if (!result.success || !result.data) {
    console.error("Failed to load lifecycle access tab:", result.error);
    throw new Error(result.error ?? "Failed to load lifecycle access tab");
  }

  const data = result.data;
  const memberRole = data.memberRole;

  const isAdminOrSuperAdmin =
    memberRole === APITypes.ManagerRolesType.SuperAdmin ||
    memberRole === APITypes.ManagerRolesType.Admin;

  const currentUserRole = authAccessInfo.data?.role;
  const canUpdateMemberRights =
    currentUserRole === APITypes.ManagerRolesType.SuperAdmin ||
    currentUserRole === APITypes.ManagerRolesType.Admin;

  return {
    accessRights: data.accessRights,
    memberID,
    memberRole,
    isAdminOrSuperAdmin,
    canUpdateMemberRights,
  };
}

export type LifecycleAccessTabLoaderType = {
  accessRights: APITypes.ResourceLifeAccessRight[];
  memberID: string;
  memberRole?: APITypes.ManagerRolesType | null;
  isAdminOrSuperAdmin: boolean;
  canUpdateMemberRights: boolean;
};

export const LifecycleAccessTabAction = async ({
  request,
  params,
}: ActionFunctionArgs) => {
  const auth = getAuth();
  const user = await getReadyAuthUser();
  const formData = await request.formData();
  const intent = formData.get("intent");
  const memberID = params.userID || "";
  const organizationID = params.organizationID || "";

  switch (intent) {
    case INTENTS._update_lifecycle_access_status: {
      const payload = JSON.parse(formData.get("body") as string) as {
        resourceType?: APITypes.EntityType;
        accessType?: string;
        status?: APITypes.ResourceAccessRightStatus;
      };
      if (
        !payload.resourceType ||
        !payload.accessType ||
        payload.status === undefined
      ) {
        return null;
      }
      const result = await apiClient.sendIntent<{
        success: boolean;
        data: unknown;
      }>(INTENTS._update_lifecycle_access_status, {
        resourceType: payload.resourceType,
        accessType: payload.accessType,
        status: payload.status,
        memberID,
        organizationID,
      });
      return result;
    }

    case INTENTS._toggle_lifecycle_access: {
      const payload = JSON.parse(formData.get("body") as string) as {
        resourceType?: APITypes.EntityType;
        accessType?: string;
        enabled?: boolean;
      };
      if (
        !payload.resourceType ||
        !payload.accessType ||
        payload.enabled === undefined
      ) {
        return null;
      }
      const result = await apiClient.sendIntent<{
        success: boolean;
        data: unknown;
      }>(INTENTS._toggle_lifecycle_access, {
        resourceType: payload.resourceType,
        accessType: payload.accessType,
        enabled: payload.enabled,
        memberID,
        organizationID,
      });
      return result;
    }

    default:
      return null;
  }
};
